import React, { useCallback, useMemo, useState } from 'react';
import { Server as ServerMenu } from '../types/menu';
import { Client, Server } from '../types/basket';
import { BaseComponentProps } from '../types';
import collect from 'collect.js';
import useMenu from '../hooks/context/useMenu';
import LineItemModal from '../modals/LineItemModal';
import useOngoingOrder from '../hooks/context/useOngoingOrder';

export type OrderBasketContextType = {
  lineItems: Client.OrderLineItem[];
  quantity: number;
  subtotal: number;
  initItem: (product: Pick<ServerMenu.Product, 'id'>) => Server.OrderLineItem;
  addItem: (value: Server.OrderLineItem) => void;
  removeItem: (index: number) => void;
  setItem: (index: number, value: Server.OrderLineItem) => void;
  updateItem: (index: number, value: Partial<Server.OrderLineItem>) => void;
  incrementItem: (index: number) => void;
  decrementItem: (index: number) => void;
  compileItem: (item: Server.OrderLineItem) => Client.OrderLineItem;
  openLineItemModal: (lineItem: Server.OrderLineItem, lineItemIndex?: null | number) => void;
};

export const OrderBasketContext = React.createContext<OrderBasketContextType>(null);

export default function OrderBasketProvider({ children }: BaseComponentProps) {
  const { orderData, updateOrderData } = useOngoingOrder();
  const { products, modifiers } = useMenu();

  const [selectedItem, setSelectedItem] = useState<null | Server.OrderLineItem>(null);
  const [selectedItemIndex, setSelectedItemIndex] = useState<null | number>(null);

  const compileItem = useCallback<(item: Server.OrderLineItem) => Client.OrderLineItem>(
    (item: Server.OrderLineItem) => {
      const withProducts = {
        ...item,
        product: products[item.product_id],
        modifiers: item.modifiers.map((modifier) => {
          const _modifier = modifiers[modifier.modifier_id];

          return {
            ...modifier,
            modifier: _modifier,
            item_unit_price: _modifier.price * item.quantity,
            item_price: _modifier.price * item.quantity * modifier.quantity,
            package_unit_price: _modifier.price * item.quantity,
            package_price: _modifier.price * item.quantity * modifier.quantity,
          };
        }),
      };

      const unitPrice = collect(withProducts.modifiers).reduce(
        (carry: number, item: Client.OrderLineItemModifier) =>
          carry + item.modifier.price * item.quantity,
        withProducts.product.price,
      );

      return {
        ...withProducts,
        item_unit_price: withProducts.product.price,
        item_price: withProducts.product.price * item.quantity,
        package_unit_price: unitPrice,
        package_price: unitPrice * item.quantity,
      };
    },
    [products, modifiers],
  );

  const _items = useMemo<Client.OrderLineItem[]>(
    () => orderData.line_items.map((item) => compileItem(item)),
    [orderData.line_items],
  );
  const quantity = useMemo<number>(() => +collect(_items).sum('quantity'), [_items]);
  const subtotal = useMemo<number>(() => +collect(_items).sum('package_price'), [_items]);

  const searchItem = useCallback(
    (item: Server.OrderLineItem) => {
      return collect(_items).search(
        (lineItem) =>
          item.product_id === lineItem.product_id &&
          item.note === lineItem.note &&
          collect(item.modifiers)
            .sortBy(
              (modifier: Server.OrderLineItemModifier) =>
                `${modifier.modifier_group_id}-${modifier.modifier_id}-${modifier.quantity}`,
            )
            .reduce(
              (carry, modifier) =>
                carry +
                `${modifier.modifier_group_id}-${modifier.modifier_id}-${modifier.quantity}`,
              '',
            ) ===
            collect(lineItem.modifiers)
              .sortBy(
                (modifier: Server.OrderLineItemModifier) =>
                  `${modifier.modifier_group_id}-${modifier.modifier_id}-${modifier.quantity}`,
              )
              .reduce(
                (carry, modifier) =>
                  carry +
                  `${modifier.modifier_group_id}-${modifier.modifier_id}-${modifier.quantity}`,
                '',
              ),
      );
    },
    [_items],
  );

  const initItem = (product: ServerMenu.Product): Server.OrderLineItem => ({
    product_id: product.id,
    modifiers: [],
    quantity: 1,
    note: null,
  });

  const addItem = (value: Server.OrderLineItem) => {
    const index = searchItem(value);

    if (typeof index === 'number' && index >= 0) {
      return incrementItem(index, value.quantity);
    }

    return updateOrderData('line_items', [...orderData.line_items, value]);
  };

  const removeItem = useCallback(
    (index: number) =>
      updateOrderData('line_items', collect(orderData.line_items).forget(index).toArray()),
    [orderData.line_items],
  );

  const setItem = useCallback(
    (index: number, value: Server.OrderLineItem) =>
      updateOrderData('line_items', collect(orderData.line_items).put(index, value).toArray()),
    [orderData.line_items],
  );

  const updateItem = useCallback(
    (index: number, value: Partial<Server.OrderLineItem>) =>
      setItem(index, {
        ...orderData.line_items[index],
        ...value,
      }),
    [orderData.line_items],
  );

  const incrementItem = useCallback(
    (index: number, quantity: number = 1) =>
      updateItem(index, { quantity: orderData.line_items[index].quantity + quantity }),
    [orderData.line_items],
  );

  const decrementItem = useCallback(
    (index: number) => {
      const quantity = orderData.line_items[index].quantity;

      if (quantity === 1) {
        return removeItem(index);
      }

      return updateItem(index, { quantity: orderData.line_items[index].quantity - 1 });
    },
    [orderData.line_items],
  );

  const openLineItemModal = useCallback(
    (lineItem: Server.OrderLineItem, lineItemIndex: null | number = null) => {
      setSelectedItem(lineItem);
      setSelectedItemIndex(lineItemIndex);
    },
    [],
  );

  const closeLineItemModal = useCallback(() => {
    setSelectedItem(null);
    setSelectedItemIndex(null);
  }, []);

  const context = useMemo(
    () => ({
      lineItems: _items ?? [],
      quantity,
      subtotal,
      initItem,
      addItem,
      removeItem,
      setItem,
      updateItem,
      incrementItem,
      decrementItem,
      compileItem,
      openLineItemModal,
    }),
    [_items, compileItem],
  );

  return (
    <OrderBasketContext.Provider value={context}>
      {children}
      {Boolean(selectedItem) && (
        <LineItemModal
          data={selectedItem}
          onConfirm={(data: Server.OrderLineItem) =>
            selectedItemIndex !== null ? setItem(selectedItemIndex, data) : addItem(data)
          }
          onClose={closeLineItemModal}
        />
      )}
    </OrderBasketContext.Provider>
  );
}
